/**
 * FSRS+ Study Preferences card — Wave 1.
 * Lives on Account page. Lets the student set:
 *   - Target exam date (global, per-course override happens on Enrollments page)
 *   - Subject interleaving toggle (default ON, research-backed)
 *   - Confidence-weighted rating UI (opt-in 4-tier)
 *   - Daily bedtime (powers sleep-aligned reviews in Wave 2)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, Shuffle, Target, Moon, Sparkles, Loader2 } from "lucide-react";

export default function StudyPrefsCard() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/study-prefs")
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs(null));
  }, []);

  const save = async (patch, successMsg) => {
    setBusy(true);
    try {
      const r = await api.patch("/me/study-prefs", patch);
      setPrefs(r.data);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) {
    return (
      <Card className="p-4 border-slate-200" data-testid="study-prefs-card-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading study preferences…
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 border-slate-200 space-y-4" data-testid="study-prefs-card">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-blue-700" />
        <h3 className="font-display font-bold text-sm">FSRS+ Study Plan</h3>
        <span className="text-[10px] uppercase tracking-[0.12em] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
          NEW
        </span>
      </div>

      {/* Exam date */}
      <div className="space-y-2">
        <Label htmlFor="exam-date" className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <CalendarDays className="w-3.5 h-3.5" /> Target exam date
        </Label>
        <div className="flex gap-2">
          <Input
            id="exam-date"
            type="date"
            value={prefs.target_exam_date || ""}
            disabled={busy}
            onChange={(e) => save({ target_exam_date: e.target.value || "" },
              e.target.value ? "Exam date saved — reviews will peak on that day" : "Exam date cleared")}
            data-testid="exam-date-input"
            className="text-sm"
          />
        </div>
        <p className="text-[11px] text-slate-500">
          Reviews are scheduled to peak the day before this date. <strong>Within 14 days</strong>, the
          engine switches to <em>cram mode</em> with shorter intervals.
        </p>
      </div>

      {/* Interleaving */}
      <div className="flex items-start gap-3 pt-1 border-t border-slate-100">
        <Shuffle className="w-4 h-4 text-blue-700 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-800">Interleave subjects</div>
            <Switch
              checked={prefs.interleave_subjects !== false}
              disabled={busy}
              onCheckedChange={(v) => save({ interleave_subjects: v },
                v ? "Subjects will be mixed in each session" : "Sessions will stay subject-by-subject")}
              data-testid="interleave-toggle"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Mix subjects within each session — proven ~30% long-term retention boost.
          </p>
        </div>
      </div>

      {/* Confidence rating UI */}
      <div className="flex items-start gap-3 pt-1 border-t border-slate-100">
        <Target className="w-4 h-4 text-blue-700 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-slate-800">Confidence rating buttons</div>
            <Switch
              checked={!!prefs.confidence_rating_ui}
              disabled={busy}
              onCheckedChange={(v) => save({ confidence_rating_ui: v },
                v ? "You'll rate confidence after each answer" : "Standard answer flow restored")}
              data-testid="confidence-toggle"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Adds a 4-button rating (<em>Wrong / Guessed / Knew it / Obvious</em>) after each answer.
            Cleaner signal for the FSRS engine — fewer reviews to hit the same retention.
          </p>
        </div>
      </div>

      {/* Bedtime */}
      <div className="flex items-start gap-3 pt-1 border-t border-slate-100">
        <Moon className="w-4 h-4 text-indigo-700 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="bedtime" className="text-sm font-medium text-slate-800">Bedtime</Label>
            <Input
              id="bedtime"
              type="time"
              className="w-32 text-sm"
              value={prefs.daily_bedtime || ""}
              disabled={busy}
              onChange={(e) => save({ daily_bedtime: e.target.value || "" })}
              data-testid="bedtime-input"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Used for sleep-aligned reviews (coming soon) — short late-afternoon sessions
            that your brain consolidates overnight.
          </p>
        </div>
      </div>
    </Card>
  );
}
